import React from 'react';
import { Button, IconButton, Tooltip } from '@material-ui/core';
import RestartAltIcon from '@material-ui/icons/Autorenew';
import { useEntity, useEntityOwnership } from '@backstage/plugin-catalog-react';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import { isAdminRole } from '../../utils/roles';
import { ManagedComponentsResolver } from '../../utils/managedComponents';

interface RestartComponentButtonProps {
  entity?: any;
  size?: 'small' | 'medium';
  render?: 'icon' | 'button';
  fullWidth?: boolean;
  label?: string;
}

const RestartComponentButtonWithContext = ({ size = 'small', render = 'icon', fullWidth, label }: Omit<RestartComponentButtonProps, 'entity'>) => {
  const { entity } = useEntity();
  return <RestartComponentButtonInternal entity={entity} size={size} render={render} fullWidth={fullWidth} label={label} />;
};

const RestartComponentButtonInternal = ({ entity, size = 'small', render = 'icon', fullWidth, label }: Required<Pick<RestartComponentButtonProps, 'entity'>> & Omit<RestartComponentButtonProps, 'entity'>) => {
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const scaffolderApi = useApi(scaffolderApiRef);
  const kubernetesApi = useApi(kubernetesApiRef);
  const { isOwnedEntity } = useEntityOwnership();
  const [isAdmin, setIsAdmin] = React.useState<boolean>(false);
  const [isExecuting, setIsExecuting] = React.useState<boolean>(false);

  // The Restart action looks up the current image tag from running pods and
  // re-deploys via `source_type: harbor`. Whether it applies is resolved from
  // the type's `supportsRestart` flag in app-config AND from the entity itself
  // -- see the resolver. A component with no image has no tag to redeploy.
  const managed = React.useMemo(
    () => new ManagedComponentsResolver(configApi),
    [configApi],
  );
  const detectedType = React.useMemo(
    () => managed.detectType(entity),
    [managed, entity],
  );
  const supportsRestart = React.useMemo(
    () => managed.supportsRestart(entity),
    [managed, entity],
  );
  const restartTemplateRef = detectedType?.templates.primary;

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const ownershipEntityRefs = identity.ownershipEntityRefs ?? [];
        const admin = isAdminRole(ownershipEntityRefs, configApi);
        if (mounted) setIsAdmin(admin);
      } catch (_e) {
        if (mounted) setIsAdmin(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [identityApi, configApi]);

  const isOwner = React.useMemo(() => {
    try {
      return !!(entity && isOwnedEntity(entity));
    } catch {
      return false;
    }
  }, [entity, isOwnedEntity]);

  // Note: Removed localStorage-based deletion tracking
  // If resources are deleted, Restart will fail naturally (no deployment to restart)

  const handleRestart = async () => {
    if (!entity) return;
    try {
      setIsExecuting(true);

      const fullEntityName = entity.metadata.name as string;
      const { baseName, targetUser } = managed.parseEntityName(entity);

      const namespace = entity.metadata.annotations?.['kubernetes.io/namespace'] as string | undefined;
      if (!namespace) {
        alert('Cannot restart: Kubernetes namespace not found in entity annotations.');
        return;
      }

      // Get current image version from Kubernetes deployment
      let currentImageTag = 'latest';
      try {
        const k8sObjects = await kubernetesApi.getObjectsByEntity({ entity });
        // Find the deployment in the cluster objects
        for (const clusterObj of k8sObjects.items) {
          const resources = (clusterObj as any).resources || [];
          for (const resource of resources) {
            if (resource.type === 'deployments') {
              const deployment = (resource.resources || []).find((dep: any) => 
                dep.metadata?.name === fullEntityName
              );
              
              if (deployment && deployment.spec?.template?.spec?.containers?.[0]?.image) {
                const image = deployment.spec.template.spec.containers[0].image;
                // Extract tag from image string (e.g., "harbor.../repo:v1.2.3" -> "v1.2.3")
                const tagMatch = image.match(/:(.+)$/);
                if (tagMatch && tagMatch[1]) {
                  currentImageTag = tagMatch[1];
                  break;
                }
              }
            }
          }
          if (currentImageTag !== 'latest') break;
        }
      } catch (err) {
        console.warn('Could not fetch current image from Kubernetes, using latest:', err);
      }

      const confirmed = window.confirm(
        `This will perform a rolling restart of "${fullEntityName}" pods with the current image version (${currentImageTag}). The application will remain available during the restart.\n\nProceed?`
      );
      if (!confirmed) {
        return;
      }

      // Use 'update' intent with harbor source to trigger a rolling restart.
      // The k8s:apply action will force a rollout even with the same image.
      if (!restartTemplateRef) return; // Should be unreachable due to button gating below
      const values: Record<string, any> = {
        deployment_intent: 'update',
        source_type: 'harbor',
        name: baseName,
        namespace,
        image_version: currentImageTag,
      };

      // See the note in UpdateComponentButton: this button also renders for
      // admins on other people's components, and currentImageTag above was
      // read off THIS entity's deployment -- so the target has to be this
      // entity's owner, not the caller.
      if (targetUser) {
        values.targetUser = targetUser;
      }

      const response = await scaffolderApi.scaffold({
        templateRef: restartTemplateRef,
        values,
      });

      if (response.taskId) {
        window.location.href = `/create/tasks/${response.taskId}`;
      }
    } catch (e) {
      console.error('Failed to trigger restart:', e);
      alert('Failed to restart application. Please try again.');
    } finally {
      setIsExecuting(false);
    }
  };

  const canRestart = isAdmin || isOwner;

  if (!entity || !supportsRestart || !restartTemplateRef || !canRestart) {
    return null;
  }

  if (render === 'button') {
    return (
      <Tooltip title={isExecuting ? 'Restarting...' : 'Restart Application Pods'}>
        <span>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleRestart}
            disabled={isExecuting}
            fullWidth={!!fullWidth}
          >
            {label || 'Restart'}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={isExecuting ? 'Restarting...' : 'Restart Application Pods'}>
      <span>
        <IconButton color="primary" onClick={handleRestart} size={size} disabled={isExecuting}>
          <RestartAltIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export const RestartComponentButton = ({ entity: propEntity, size = 'small', render = 'icon', fullWidth, label }: RestartComponentButtonProps) => {
  if (propEntity) {
    return <RestartComponentButtonInternal entity={propEntity} size={size} render={render} fullWidth={fullWidth} label={label} />;
  }
  return <RestartComponentButtonWithContext size={size} render={render} fullWidth={fullWidth} label={label} />;
};



